import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const COLORS = ["#0f766e", "#b45309", "#be123c", "#4338ca", "#0369a1", "#65a30d", "#7c3aed"];

const CARDS = [
  ["total", "Total Beneficiaries", "एकूण लाभार्थी", ""],
  ["eligible", "Eligible", "पात्र", "tab=eligible"],
  ["ineligible", "Ineligible", "अपात्र", "tab=ineligible"],
  ["error", "Error / Deficiency", "त्रुटी", "tab=error"],
  ["without_bank", "Without Bank Account", "बँक खाते नाही", "tab=no_bank"],
  ["first_paid", "1st Installment Paid", "पहिला हप्ता जमा", "first_installment_status=Paid"],
  ["first_pending", "1st Installment Pending", "पहिला हप्ता प्रलंबित", "eligibility_status=Eligible&first_installment_status=Pending"],
  ["second_paid", "2nd Installment Paid", "दुसरा हप्ता जमा", "second_installment_status=Paid"],
  ["second_pending", "2nd Installment Pending", "दुसरा हप्ता प्रलंबित", "eligibility_status=Eligible&second_installment_status=Pending"],
  ["attendance_submitted", "Attendance Submitted", "उपस्थिती सादर", "attendance_status=Submitted"],
  ["attendance_not_submitted", "Attendance Not Submitted", "उपस्थिती सादर नाही", "attendance_status=Not Submitted"],
];

const Card = ({ children, title, sub }) => (
  <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="mb-3">
      <h3 className="text-base font-semibold text-[#0f2545] md:text-lg">{title}</h3>
      {sub && <p className="text-xs text-slate-500">{sub}</p>}
    </div>
    {children}
  </section>
);

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!data) return <div className="flex h-64 items-center justify-center text-slate-500"><Loader2 className="mr-2 animate-spin" /> Loading dashboard…</div>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-serif text-3xl font-semibold text-[#0f2545] sm:text-4xl">Dashboard</h1>
        <p className="text-sm text-slate-500">Live counts from the master beneficiary database · थेट आकडेवारी</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
        {CARDS.map(([key, label, mr, qs]) => (
          <Link key={key} to={`/beneficiaries${qs ? `?${qs}` : ""}`} data-testid={`card-${key}`}
            className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
            <div className="text-2xl font-semibold text-[#0f2545]">{data.cards[key]}</div>
            <div className="mt-1 text-xs font-medium text-slate-700">{label}</div>
            <div className="text-[11px] text-slate-400">{mr}</div>
          </Link>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Beneficiary Status" sub="पात्रता स्थिती">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={data.charts.eligibility} dataKey="count" nameKey="name" outerRadius={85} label>
                {data.charts.eligibility.map((entry) => <Cell key={entry.name} fill={COLORS[data.charts.eligibility.indexOf(entry) % COLORS.length]} />)}
              </Pie>
              <Tooltip /><Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Attendance" sub="उपस्थिती">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={data.charts.attendance} dataKey="count" nameKey="name" outerRadius={85} label>
                {data.charts.attendance.map((entry) => <Cell key={entry.name} fill={COLORS[(data.charts.attendance.indexOf(entry) + 2) % COLORS.length]} />)}
              </Pie>
              <Tooltip /><Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
        <Card title="First Installment" sub="पहिला हप्ता · Paid = अधिकृत यादीनुसार, Pending = पात्र लाभार्थी">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.charts.first_installment}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} /><Tooltip />
              <Bar dataKey="count" fill="#0f2545" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Second Installment" sub="दुसरा हप्ता · Paid = अधिकृत यादीनुसार, Pending = पात्र लाभार्थी">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.charts.second_installment}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} /><Tooltip />
              <Bar dataKey="count" fill="#b45309" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Taluka-wise Beneficiaries" sub="तालुकानिहाय">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.charts.taluka} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" fontSize={11} /><YAxis type="category" dataKey="name" fontSize={11} width={90} /><Tooltip />
              <Bar dataKey="count" fill="#0f766e" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Institution-wise (Top 10)" sub="संस्थानिहाय">
          <div className="max-h-[280px] overflow-auto">
            <table className="w-full text-sm">
              <tbody>
                {data.charts.institution.map((r) => (
                  <tr key={r.name} className="border-b border-slate-100">
                    <td className="py-2 pr-2 text-slate-700">{r.name}</td>
                    <td className="py-2 text-right font-medium text-[#0f2545]">{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Card title="Recent Activity" sub="अलीकडील क्रिया">
        <div className="space-y-2">
          {data.recent_activity.map((l) => (
            <div key={`${l.created_at}-${l.user}-${l.action}`} className="flex flex-wrap items-center gap-2 border-b border-slate-100 pb-2 text-xs text-slate-600">
              <span className="font-medium text-[#0f2545]">{l.user}</span>
              <span className="rounded bg-slate-100 px-1.5 py-0.5">{l.module}</span>
              <span>{l.action}</span>
              {l.beneficiary_id && <span className="font-mono">{l.beneficiary_id}</span>}
              <span className="ml-auto text-slate-400">{new Date(l.created_at).toLocaleString("en-IN")}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
